import React, { useId, useState } from 'react';
import { Link } from 'react-router-dom';
import { ChevronDown } from '../ui/icons';
import { formatPrice } from '../../content/tiers';

/**
 * PricingCard — the Figma "type=pricing" component (Mixtape Memories file, node 60:2),
 * in three colourways so the three offers read as distinct, not one card recoloured:
 *
 *   blue    retro-blue #4A6A7A, paper text   (the Figma original)
 *   orange  coral-deep #C4472A, paper text   (coral-deep, not coral: paper text on
 *                                             #E15B3B is under 4.5:1)
 *   paper   warm light grey, ink text, 2px ink border (so it holds its edge on a
 *                                             cream page)
 *
 * From Figma: 26px padding, 8px radius, hard 6px ink shadow, a 1px ink scanline every
 * 4px, a 12px Space Mono Bold eyebrow (tracking 0.72px), a 27px Archivo Black price,
 * 13.5px ✓ rows and a bold 14px "Get started".
 *
 * "How it works" is a separate disclosure button — it only shows and hides the
 * tier's steps, so browsing can never start a purchase by accident. "Get started"
 * is the only way forward, and is a plain link.
 */

const VARIANTS = {
  blue: {
    card: 'bg-brand-retro-blue text-brand-paper',
    scan: 0.18,
    cta: 'bg-brand-paper text-brand-ink hover:bg-brand-cream',
    toggle: 'text-brand-paper',
    heading: 'text-brand-paper',
    divider: 'border-brand-paper/30',
    focus: 'focus-visible:outline-brand-paper',
  },
  orange: {
    card: 'bg-brand-coral-deep text-brand-paper',
    // Lighter lines here: the orange starts closer to the AA line for paper text.
    scan: 0.1,
    cta: 'bg-brand-paper text-brand-ink hover:bg-brand-cream',
    toggle: 'text-brand-paper',
    heading: 'text-brand-paper',
    divider: 'border-brand-paper/30',
    focus: 'focus-visible:outline-brand-paper',
  },
  paper: {
    card: 'bg-[#ECE8E1] text-brand-ink border-2 border-brand-ink',
    scan: 0.07,
    cta: 'bg-brand-ink text-brand-paper hover:bg-brand-secondary-ink',
    toggle: 'text-brand-ink',
    heading: 'text-brand-ink',
    divider: 'border-brand-ink/25',
    focus: 'focus-visible:outline-brand-ink',
  },
};

const scanlines = (opacity) => ({
  backgroundImage: `repeating-linear-gradient(to bottom, rgba(36, 26, 18, ${opacity}) 0, rgba(36, 26, 18, ${opacity}) 1px, transparent 1px, transparent 4px)`,
});

/**
 * @param tier   an entry from content/tiers.json
 * @param action { to, state } — where "Get started" goes (decided by the section,
 *               which knows whether the visitor is signed in and whether the tier
 *               can be ordered online yet)
 * @param headingLevel  'h3' on the homepage (under the section's h2)
 * @param ctaLabel  the button's words: "Get started" on the homepage, "Start new project"
 *                  in the signed-in app (the dashboard and the "+ New Video" chooser)
 */
const PricingCard = ({ tier, action, headingLevel: Heading = 'h3', open: openProp, onToggle, ctaLabel = 'Get started' }) => {
  // Controlled by PricingSection so "How it works" opens and closes on all three cards
  // together (founder request, 2026-09-27). Falls back to its own state when used alone.
  const [ownOpen, setOwnOpen] = useState(false);
  const controlled = typeof openProp === 'boolean';
  const open = controlled ? openProp : ownOpen;
  const toggle = () => (controlled ? onToggle?.() : setOwnOpen((o) => !o));
  const panelId = useId();
  const v = VARIANTS[tier.variant] || VARIANTS.blue;

  return (
    <article
      className={`relative flex flex-col rounded-[8px] p-[26px] shadow-hard-lg ${v.card}`}
      style={scanlines(v.scan)}
      aria-labelledby={`${panelId}-name`}
      data-tier={tier.id}
    >
      {/* Explicit colour: the global h1–h4 rule (index.css) would otherwise paint
          this ink on the blue and orange cards. */}
      <Heading
        id={`${panelId}-name`}
        className={`font-mono font-bold uppercase text-[12px] leading-none tracking-[0.72px] ${v.heading}`}
      >
        {tier.name}
      </Heading>

      <p className="mt-4 font-display text-[27px] leading-none">
        {formatPrice(tier.priceCents)}
        <span className="sr-only"> Australian dollars, including GST</span>
      </p>

      <ul className="mt-5 space-y-2.5 font-mono text-[13.5px] leading-snug">
        {tier.features.map((f) => (
          <li key={f} className="flex gap-2.5">
            <span aria-hidden="true" className="flex-shrink-0">✓</span>
            <span>{f}</span>
          </li>
        ))}
      </ul>

      {/* Pushes the controls to the bottom so the three cards line up. */}
      <div className="mt-auto pt-6">
        <button
          type="button"
          onClick={toggle}
          aria-expanded={open}
          aria-controls={`${panelId}-how`}
          className={`inline-flex items-center gap-1.5 font-mono text-[13px] underline underline-offset-4 decoration-1 rounded-sm focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 ${v.toggle} ${v.focus}`}
        >
          How it works
          <ChevronDown
            className={`w-4 h-4 transition-transform duration-200 motion-reduce:transition-none ${open ? 'rotate-180' : ''}`}
            aria-hidden="true"
          />
        </button>

        <div id={`${panelId}-how`} hidden={!open} className={`mt-4 border-t pt-4 ${v.divider}`}>
          <p className="font-mono text-[13px] font-bold leading-snug">{tier.effort}</p>
          <ol className="mt-3 space-y-2 font-mono text-[13px] leading-snug">
            {tier.steps.map((step, i) => (
              <li key={step} className="flex gap-2.5">
                <span aria-hidden="true" className="flex-shrink-0">{i + 1}.</span>
                <span>{step}</span>
              </li>
            ))}
          </ol>
        </div>

        <Link
          to={action.to}
          state={action.state}
          className={`mt-5 block w-full rounded-[6px] py-3 text-center font-mono font-bold text-[14px] shadow-hard-sm transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 ${v.cta} ${v.focus}`}
        >
          {ctaLabel}<span className="sr-only"> with {tier.name}</span>
        </Link>
      </div>
    </article>
  );
};

export default PricingCard;
